// Orchestrates the offline outbox: queueing a run composed with the network
// off, and draining it back to POST /api/sync/push once reconnected. See
// outbox.ts for the IndexedDB storage and platform.ts (API side) for the
// push/pull engine this talks to.
import { api } from "./api";
import { getSyncCursor, setSyncCursor } from "./storage";
import { uuid } from "./uuid";
import { putPhotoOnce, type PhotoPurpose } from "./photos";
import {
  cacheRun, enqueueEvent, enqueuePhoto, listEvents, listQueuedPhotos, markEventError, markPhotoError,
  markRunSynced, removeEvent, removeQueuedPhoto,
  type CachedRun, type SyncEvent,
} from "./outbox";

export type RunFields = {
  client_uuid: string;
  bag_id?: string | null;
  mode: string;
  started_at: number;
  stopped_at: number;
  active_ms: number;
  paused_ms: number;
  end_grams: number | null;
  strain_id: number | null;
  strain_name: string | null;
  trim_type: string | null;
  nug_size: string | null;
  job_id: number | null;
  site_id?: number | null;
  local_gpm: number | null;
  // Full-batch-only, additive — see runs-routes.ts's CHECK note: v5's runs
  // table has no photo/start-weight columns, so these ride in the sync
  // event's payload only (audit-preserved, never projected onto the row).
  start_grams?: number | null;
  start_photo_id?: string | null;
  end_photo_id?: string | null;
  qa_photo_id?: string | null;
};

/**
 * Compose a run finished offline as a run.created sync event and drop it in
 * the outbox, plus an optimistic CachedRun so the home screen's recent-runs
 * list shows it immediately (marked pending) instead of looking like the
 * save silently vanished.
 *
 * KNOWN GAP (server-side, out of this milestone's sanctioned API changes):
 * platform.ts's applyEvent() "run.created" branch — the code path a queued
 * event replays through on push — only projects client_uuid/player_id/mode/
 * started_at/stopped_at/paused_ms/active_ms/start_grams/end_grams/device_id
 * onto the new row. It does NOT read trim_type/nug_size or attach job_id the
 * way the direct POST /api/runs handler does. So a run saved OFFLINE lands
 * server-side without its trim-style/nug-size grading or job attachment,
 * even though this payload carries them (kept for forward-compat + the audit
 * trail — see the report to the orchestrator).
 */
export async function queueRunOffline(
  fields: RunFields, player: { id: number; name: string }, deviceId: string,
): Promise<CachedRun> {
  const eventId = uuid();
  await enqueueEvent({
    event_id: eventId,
    type: "run.created",
    entity_type: "run",
    entity_id: fields.client_uuid,
    actor_player_id: player.id,
    device_id: deviceId,
    occurred_at: Date.now(),
    payload: {
      client_uuid: fields.client_uuid,
      ...(fields.bag_id ? { bag_id: fields.bag_id } : {}),
      player_id: player.id,
      mode: fields.mode,
      started_at: fields.started_at,
      stopped_at: fields.stopped_at,
      active_ms: fields.active_ms,
      paused_ms: fields.paused_ms,
      start_grams: fields.start_grams ?? null,
      end_grams: fields.end_grams,
      strain_id: fields.strain_id,
      device_id: deviceId,
      trim_type: fields.trim_type,
      nug_size: fields.nug_size,
      job_id: fields.job_id,
      ...(fields.site_id ? { site_id: fields.site_id } : {}),
      ...(fields.start_photo_id ? { start_photo_id: fields.start_photo_id } : {}),
      ...(fields.end_photo_id ? { end_photo_id: fields.end_photo_id } : {}),
      ...(fields.qa_photo_id ? { qa_photo_id: fields.qa_photo_id } : {}),
    },
  });
  const cached: CachedRun = {
    client_uuid: fields.client_uuid,
    player_name: player.name,
    strain_name: fields.strain_name,
    end_grams: fields.end_grams,
    gpm: fields.local_gpm,
    mode: fields.mode,
    created_at: Math.floor(Date.now() / 1000),
    pending: true,
  };
  await cacheRun(cached);
  return cached;
}

type PushResult = {
  accepted: string[];
  duplicate: string[];
  rejected: { event_id: string | null; error: string }[];
  pending_gap: string[];
  server_time: number;
};

function toWireEvent(ev: SyncEvent) {
  const { queued_at: _q, attempts: _a, last_error: _e, ...wire } = ev;
  return wire;
}

const PHOTO_ID_PAYLOAD_KEYS = ["start_photo_id", "end_photo_id", "qa_photo_id"] as const;

/** Queues a full-batch photo captured offline (or whose immediate PUT
 * attempt failed) for the background retry loop below. */
export async function queuePhotoOffline(
  photoId: string, purpose: PhotoPurpose, blob: Blob, contentType: string, deviceId: string,
): Promise<void> {
  await enqueuePhoto({ photo_id: photoId, purpose, content_type: contentType, data: blob, device_id: deviceId });
}

/** Drains the photo outbox — one whole-file PUT attempt per queued photo.
 * Committed uploads are removed; anything else stays queued (with its error
 * noted) for the next tick. Always called BEFORE flushOutbox below sends any
 * run event, so a run's start_photo_id/end_photo_id/qa_photo_id can never
 * reach the server ahead of the bytes they point at. */
export async function flushPhotoOutbox(deviceId: string): Promise<{ flushed: number; remaining: number }> {
  if (!navigator.onLine) return { flushed: 0, remaining: (await listQueuedPhotos()).length };
  const rows = await listQueuedPhotos();
  let flushed = 0;
  for (const row of rows) {
    try {
      const bytes = new Uint8Array(await row.data.arrayBuffer());
      const result = await putPhotoOnce(row.photo_id, bytes, row.content_type, row.purpose as PhotoPurpose, deviceId);
      if (result.committed) {
        await removeQueuedPhoto(row.photo_id);
        flushed += 1;
      } else {
        await markPhotoError(row.photo_id, "not committed after whole-file PUT");
      }
    } catch (err) {
      if (err instanceof Error && err.message.endsWith("HTTP 402")) workspacePaused = true;
      await markPhotoError(row.photo_id, err instanceof Error ? err.message : "upload failed");
    }
  }
  const remaining = await listQueuedPhotos();
  return { flushed, remaining: remaining.length };
}

/** Best-effort linkage for an ONLINE full-batch save: POST /api/runs already
 * created the row (with job credit / gpm / yield_pct in its own response),
 * but its handler has nowhere to put photo ids (no columns — see the CHECK
 * note in runs-routes.ts) and doesn't echo arbitrary extra form fields into
 * its own event payload either. So this fires a small additive run.updated
 * sync event carrying just the photo ids, keyed by the server row id —
 * processPush's writeTriplet stores the FULL payload as `after`, so the
 * linkage lands in the audit trail even though the runs row itself never
 * gains photo columns. Never awaited by the save path — a failure here loses
 * photo linkage, not the run itself. */
export async function linkFullBatchPhotos(
  runId: number,
  ids: { start_photo_id: string | null; end_photo_id: string | null; qa_photo_id: string | null },
  deviceId: string,
): Promise<void> {
  const payload: Record<string, string> = {};
  if (ids.start_photo_id) payload.start_photo_id = ids.start_photo_id;
  if (ids.end_photo_id) payload.end_photo_id = ids.end_photo_id;
  if (ids.qa_photo_id) payload.qa_photo_id = ids.qa_photo_id;
  if (Object.keys(payload).length === 0) return;
  await api.postJson("/api/sync/push", {
    device_id: deviceId,
    tenant_id: 1,
    events: [{
      event_id: uuid(),
      type: "run.updated",
      entity_type: "run",
      entity_id: runId,
      device_id: deviceId,
      occurred_at: Date.now(),
      payload,
    }],
  });
}

let flushing = false;
let workspacePaused = false;

/** True while the server is answering sync/photo uploads with 402 (workspace
 * locked: trial expired or unpaid). Cleared by the next successful push. */
export function isWorkspacePaused(): boolean {
  return workspacePaused;
}

/** Drains the outbox. No-ops (leaving the queue untouched) if offline, a
 * flush is already in flight, or the push request itself fails at the
 * network level — accepted/duplicate events are removed and their cached
 * run flipped off "pending"; rejected events are kept but annotated with the
 * server's error so they stay visible instead of silently vanishing.
 *
 * Photo-before-run ordering: photos are flushed first (see flushPhotoOutbox
 * above), then any run event that still references a photo id NOT YET
 * cleared from the photo outbox is held back this round — left queued,
 * untouched — rather than sent with a dangling reference. It's picked up
 * again next tick once its photo(s) land. */
export async function flushOutbox(deviceId: string): Promise<{ flushed: number; remaining: number }> {
  if (flushing || !navigator.onLine) {
    return { flushed: 0, remaining: (await listEvents()).length };
  }
  flushing = true;
  try {
    await flushPhotoOutbox(deviceId);

    const events = await listEvents();
    if (events.length === 0) return { flushed: 0, remaining: 0 };
    const sorted = [...events].sort((a, b) => a.occurred_at - b.occurred_at);

    const pendingPhotoIds = new Set((await listQueuedPhotos()).map((p) => p.photo_id));
    const blockedByPhoto = (ev: SyncEvent): boolean => {
      const payload = ev.payload as Record<string, unknown>;
      return PHOTO_ID_PAYLOAD_KEYS.some((key) => {
        const v = payload[key];
        return typeof v === "string" && pendingPhotoIds.has(v);
      });
    };
    const sendable = sorted.filter((e) => !blockedByPhoto(e));
    if (sendable.length === 0) return { flushed: 0, remaining: sorted.length };

    let body: PushResult;
    try {
      const res = await api.postJson("/api/sync/push", {
        device_id: deviceId,
        tenant_id: 1,
        events: sendable.map(toWireEvent),
      });
      // 402 = workspace locked (trial expired / unpaid). Events stay queued
      // and the retry loop keeps going; the flag just lets the UI say why.
      workspacePaused = res.status === 402;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      body = await res.json();
    } catch {
      return { flushed: 0, remaining: sorted.length };
    }

    const settled = new Set([...body.accepted, ...body.duplicate]);
    for (const ev of sendable) {
      if (settled.has(ev.event_id)) {
        await removeEvent(ev.event_id);
        if (ev.type === "run.created") await markRunSynced(ev.entity_id);
      }
    }
    for (const r of body.rejected) {
      if (r.event_id) await markEventError(r.event_id, r.error);
    }
    const remaining = await listEvents();
    return { flushed: settled.size, remaining: remaining.length };
  } finally {
    flushing = false;
  }
}

/** Pull-on-open: advances the stored (received_at, event_id) cursor and
 * reports whether anything new came down. Deliberately doesn't try to
 * re-derive run rows from raw events client-side — GET /api/runs already
 * returns fully joined, server-computed rows, so pull's job here is just to
 * detect "something changed" (e.g. a manager edited one of my runs while I
 * was offline) and let the caller decide to refetch. */
export async function pullSince(orgId: number): Promise<{ hasNew: boolean }> {
  if (!navigator.onLine) return { hasNew: false };
  const cursor = getSyncCursor(orgId);
  try {
    const res = await api.postJson("/api/sync/pull", { ...cursor, limit: 200 });
    if (!res.ok) return { hasNew: false };
    const body: { events: Array<{ received_at: number; event_id: string }> } = await res.json();
    if (body.events.length === 0) return { hasNew: false };
    const last = body.events[body.events.length - 1];
    setSyncCursor(orgId, { after_received_at: last.received_at, after_event_id: last.event_id });
    return { hasNew: true };
  } catch {
    return { hasNew: false };
  }
}

export async function outboxCount(): Promise<number> {
  return (await listEvents()).length;
}

const RETRY_MS = 15000;

/** Fixed-interval retry + immediate attempt on enqueue/reconnect — same
 * shape as legacy static/js/sync.js's syncNow() (no exponential backoff;
 * the single-flight `flushing` guard in flushOutbox is what stops it from
 * hammering the API). Returns a cleanup function for a useEffect. */
export function startBackgroundSync(deviceId: string, onSettled?: () => void): () => void {
  const tick = () => {
    flushOutbox(deviceId).then(() => onSettled?.());
  };
  window.addEventListener("online", tick);
  const interval = setInterval(() => {
    if (navigator.onLine) tick();
  }, RETRY_MS);
  tick();
  return () => {
    window.removeEventListener("online", tick);
    clearInterval(interval);
  };
}
